import { createHttpLink, split, TypePolicies } from '@apollo/client';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/cache';
import { ApolloClient } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import merge from 'deepmerge';
import fetch from 'isomorphic-unfetch';
import { NextPageContext } from 'next';
import * as R from 'ramda';
import { useMemo } from 'react';

import { apiConfig } from '@config';
import {
  getUsableStoredToken,
  isGuestJourneyPath,
} from '@modules/auth/auth.session';
import { typePolicies as draftReturnTypePolicies } from '@modules/draft-return/draft-return.state';
import {
  getSubdomainFromContext,
  getSubdomainFromLocation,
} from '@modules/retailer/retailer.utils';

export interface CustomPageContext extends NextPageContext {
  apolloClient: ApolloClient<NormalizedCacheObject> | null;
}

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

export const typePolicies = R.reduce<TypePolicies, TypePolicies>(
  R.mergeDeepRight,
  {},
  [draftReturnTypePolicies],
);

const getUserData = () => getUsableStoredToken();

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

const create = (ctx: NextPageContext, initialState: any) => {
  const isBrowser = typeof window !== 'undefined';
  const fetchClient = (!isBrowser &&
    fetch) as WindowOrWorkerGlobalScope['fetch'];

  const uploadLink = createUploadLink({
    uri: apiConfig.url,
    credentials: 'same-origin',
    fetch,
  });

  // FIXME: we should avoid mixing various apollo links when it's not necessary
  const httpLink = split(
    () => true,
    uploadLink,
    createHttpLink({
      uri: apiConfig.url,
      credentials: 'same-origin',
      fetch: fetchClient,
    }),
  );

  const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    const tokenExpired = graphQLErrors?.some(({ message }) =>
      message.includes('Token expired!'),
    );

    if (!tokenExpired) {
      return;
    }

    if (
      window.location.pathname !== '/' &&
      window.location.pathname !== '/search-order'
    ) {
      // Full reload: every query of the landing page runs again.
      apolloClient?.clearStore().then(() => {
        localStorage.removeItem('token');
        window.location.href = '/';
      });
      return;
    }

    // On the landing pages, drop the token and retry the same request
    // anonymously so the page (retailer configuration etc.) still renders.
    localStorage.removeItem('token');
    operation.setContext(
      ({ headers = {} }: { headers?: Record<string, string> }) => ({
        headers: R.omit(['Authorization'], headers),
      }),
    );
    return forward(operation);
  });

  const authLink = setContext((_, { headers }) => {
    const { token, guestSessionExpired } = getUserData();

    // The guest session ended in the middle of a journey: start over cleanly
    // (the next order lookup resumes an unfinished draft return).
    if (
      guestSessionExpired &&
      isBrowser &&
      isGuestJourneyPath(window.location.pathname)
    ) {
      window.location.href = '/';
    }

    const subdomain = !isBrowser
      ? getSubdomainFromContext(ctx)
      : getSubdomainFromLocation();

    const enrichedHeaders = { ...headers };
    enrichedHeaders['X-Retailer'] = subdomain;

    if (token) {
      enrichedHeaders['Authorization'] = `Bearer ${token}`;
    }

    return {
      headers: enrichedHeaders,
    };
  });

  return new ApolloClient({
    connectToDevTools: isBrowser,
    ssrMode: !isBrowser,
    link: authLink.concat(errorLink).concat(httpLink),
    cache: new InMemoryCache({ typePolicies }).restore(initialState ?? {}),
    resolvers: {},
  });
};

export function initializeApollo(ctx: any, initialState = null) {
  const _apolloClient = apolloClient ?? create(ctx, initialState);

  // If page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();

    // Merge the initialState from getStaticProps/getServerSideProps in the existing cache
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: any, sourceArray: any) => [
        ...sourceArray,
        ...destinationArray.filter((d: any) =>
          sourceArray.every((s: any) => !R.equals(d, s)),
        ),
      ],
    });

    // Restore the cache with the merged data
    _apolloClient.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;

  return _apolloClient;
}

export function addApolloState(client: any, pageProps: any) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo(pageProps: any) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];
  const store = useMemo(() => initializeApollo(state), [state]);
  return store;
}
